/**
 * 单位换算模块 - 常量定义
 * 
 * @module types/unit-conversion/constants
 * @description 定义单位换算相关的常量和映射
 * 
 * 数据来源：
 * - backend/app/Modules/Material/Models/MaterialUnit.php (静态属性 $typeMap, $unitNameMap)
 * 
 * 注意事项：
 * - 常量名使用 UPPER_SNAKE_CASE
 * - 枚举值必须与后端完全一致
 * - 用于前端显示时的中文转换
 * 
 * 更新时机：
 * - MaterialUnit.php 静态属性变化时
 * - 新增单位类型或常用单位时
 * 
 * 验证方式：
 * 1. 对照 MaterialUnit.php 的 $typeMap
 * 2. 对照 MaterialUnit.php 的 $unitNameMap
 * 3. 确保所有枚举值都有对应的中文标签
 * 
 * @last-updated 2025-10-19
 */

import { UnitType, CommonUnit, UnitStatus } from './enums';

/**
 * 单位类型中文标签
 */
export const UNIT_TYPE_LABELS: Record<UnitType, string> = {
  [UnitType.BASE]: '基本单位',
  [UnitType.PURCHASE]: '采购单位',
  [UnitType.PRODUCTION]: '生产单位',
};

/**
 * 常用单位中文名称
 */
export const COMMON_UNIT_LABELS: Record<CommonUnit, string> = {
  [CommonUnit.PCS]: '个',
  [CommonUnit.BOX]: '箱',
  [CommonUnit.REEL]: '盘',
  [CommonUnit.KG]: '千克',
  [CommonUnit.T]: '吨',
  [CommonUnit.G]: '克',
  [CommonUnit.L]: '升',
  [CommonUnit.ML]: '毫升',
  [CommonUnit.M]: '米',
  [CommonUnit.M2]: '平方米',
  [CommonUnit.M3]: '立方米',
};

/**
 * 单位状态中文标签
 */
export const UNIT_STATUS_LABELS: Record<UnitStatus, string> = {
  [UnitStatus.INACTIVE]: '停用',
  [UnitStatus.ACTIVE]: '启用',
};

/**
 * 单位类型选项（用于下拉选择）
 */
export const UNIT_TYPE_OPTIONS = [
  { value: UnitType.BASE, label: UNIT_TYPE_LABELS[UnitType.BASE] },
  { value: UnitType.PURCHASE, label: UNIT_TYPE_LABELS[UnitType.PURCHASE] },
  { value: UnitType.PRODUCTION, label: UNIT_TYPE_LABELS[UnitType.PRODUCTION] },
];

/**
 * 常用单位选项（用于下拉选择）
 */
export const COMMON_UNIT_OPTIONS = [
  { value: CommonUnit.PCS, label: COMMON_UNIT_LABELS[CommonUnit.PCS] },
  { value: CommonUnit.BOX, label: COMMON_UNIT_LABELS[CommonUnit.BOX] },
  { value: CommonUnit.REEL, label: COMMON_UNIT_LABELS[CommonUnit.REEL] },
  { value: CommonUnit.KG, label: COMMON_UNIT_LABELS[CommonUnit.KG] },
  { value: CommonUnit.T, label: COMMON_UNIT_LABELS[CommonUnit.T] },
  { value: CommonUnit.G, label: COMMON_UNIT_LABELS[CommonUnit.G] },
  { value: CommonUnit.L, label: COMMON_UNIT_LABELS[CommonUnit.L] },
  { value: CommonUnit.ML, label: COMMON_UNIT_LABELS[CommonUnit.ML] },
  { value: CommonUnit.M, label: COMMON_UNIT_LABELS[CommonUnit.M] },
  { value: CommonUnit.M2, label: COMMON_UNIT_LABELS[CommonUnit.M2] },
  { value: CommonUnit.M3, label: COMMON_UNIT_LABELS[CommonUnit.M3] },
];

/**
 * 单位状态选项（用于下拉选择）
 */
export const UNIT_STATUS_OPTIONS = [
  { value: UnitStatus.ACTIVE, label: UNIT_STATUS_LABELS[UnitStatus.ACTIVE] },
  { value: UnitStatus.INACTIVE, label: UNIT_STATUS_LABELS[UnitStatus.INACTIVE] },
];

